import Vue from "vue"
import Vuex from "vuex"
import Constants from "@/constants"
import { diagnosticYears, AuthenticationError, BadRequestError } from "../utils"

Vue.use(Vuex)

const headers = {
  "X-CSRFToken": window.CSRF_TOKEN || "",
  "Content-Type": "application/json",
}

// Rôles matérialisés par un rattachement à une cantine (CanteenMembership) : pour eux, la
// source de vérité est la liste des rattachements, jamais `loggedUser.job`.
const PIVOT_ROLES = ["SUPERVISOR", "WORKER", "COOK", "NUTRITIONIST", "SURPLUSMANAGER"]
// Rôles pris en charge par le shell Labricot (barre fine + drawer). Depuis « tout burger partout »,
// tous les rôles connectés y passent — plus aucun n'est servi par l'ancien bandeau `AppHeader`,
// désormais réservé aux pages publiques (visiteur non connecté = `userRoles` vide → bandeau).
// Le convive en faisait déjà partie (§9.3) ; s'y ajoutent les rôles jadis sur le bandeau.
const LABRICOT_ROLES = [
  "MANAGER",
  "WORKER",
  "SUPERVISOR",
  "CONSUMER",
  "COOK",
  "NUTRITIONIST",
  "SURPLUSMANAGER",
  "RESEARCHER",
]

const LOCAL_STORAGE_VERSION = "1"
const LOCAL_STORAGE_KEY = `diagnostics-local-${LOCAL_STORAGE_VERSION}`

const verifyResponse = function(response) {
  const contentType = response.headers.get("content-type")
  const hasJSON = contentType && contentType.startsWith("application/json")

  if (response.status < 200 || response.status >= 400) {
    if (response.status === 403) throw new AuthenticationError()
    else if (response.status === 400) {
      if (hasJSON) {
        throw new BadRequestError(response.json())
      } else {
        throw new BadRequestError()
      }
    } else throw new Error(`API responded with status of ${response.status}`)
  }

  return hasJSON ? response.json() : response.text()
}

export default new Vuex.Store({
  state: {
    loggedUser: null,
    pageTitleSuffix: "ma cantine",

    userLoadingStatus: Constants.LoadingStatus.IDLE,
    canteensLoadingStatus: Constants.LoadingStatus.IDLE,
    purchasesLoadingStatus: Constants.LoadingStatus.IDLE,
    expeLoadingStatus: Constants.LoadingStatus.IDLE,
    communityEventsLoadingStatus: Constants.LoadingStatus.IDLE,
    videoTutorialsLoadingStatus: Constants.LoadingStatus.IDLE,

    sectors: [],
    sectorCategories: [],
    userCanteenPreviews: [],
    initialDataLoaded: false,
    upcomingCommunityEvents: [],
    videoTutorials: [],
    partnerTypes: [],
    lineMinistries: [],

    notifications: [],
    userPermissions: [],
    userGroups: [],

    showWebinaireBanner: false,

    // Service management
    services: [],
    servicesLoadingStatus: Constants.LoadingStatus.IDLE,
    servicesError: null,

    // Canteen services (services par cantine)
    canteenServices: [],
    canteenServicesLoadingStatus: Constants.LoadingStatus.IDLE,
    canteenServicesError: null,

    // Absences
    absences: [],
    absenceLevels: [],

    // Mode borne (kiosque) : masque header/footer et déclenche le plein écran
    kioskActive: false,

    // Cantine de contexte sélectionnée dans un dashboard Labricot (DashboardContextHeader).
    // Remontée ici pour que le drawer global (AppNavDrawer) puisse construire des liens
    // dépendants de la cantine courante (« Modifier ma cantine ») hors du dashboard.
    // Forme : { id, name } ou null.
    contextCanteen: null,
  },

  mutations: {
    SET_USER_LOADING_STATUS(state, status) {
      state.userLoadingStatus = status
    },
    SET_CANTEENS_LOADING_STATUS(state, status) {
      state.canteensLoadingStatus = status
    },
    SET_PURCHASES_LOADING_STATUS(state, status) {
      state.purchasesLoadingStatus = status
    },
    SET_EXPE_LOADING_STATUS(state, status) {
      state.expeLoadingStatus = status
    },
    SET_COMMUNITY_EVENTS_LOADING_STATUS(state, status) {
      state.communityEventsLoadingStatus = status
    },
    SET_VIDEO_TUTORIALS_LOADING_STATUS(state, status) {
      state.videoTutorialsLoadingStatus = status
    },
    SET_LOGGED_USER(state, loggedUser) {
      state.loggedUser = loggedUser
    },
    SET_KIOSK_ACTIVE(state, active) {
      state.kioskActive = !!active
    },
    SET_CONTEXT_CANTEEN(state, canteen) {
      // Ne conserve que ce dont le drawer a besoin (id + nom), ou null pour réinitialiser.
      state.contextCanteen = canteen && canteen.id ? { id: canteen.id, name: canteen.name || "" } : null
    },
    SET_SECTORS(state, sectors) {
      state.sectors = sectors
      state.sectorCategories = []
      state.sectors.forEach((s) => {
        if (!s.category) s.category = "inconnu"
        if (state.sectorCategories.indexOf(s.category) === -1) {
          state.sectorCategories.push(s.category)
        }
      })
      // alphabetical order with other and unknown at the bottom
      state.sectorCategories.sort((a, b) => {
        if (a === b) return 0
        if (a === "inconnu") return 1
        else if (b === "inconnu") return -1
        else if (a === "autres") return 1
        else if (b === "autres") return -1
        else if (a < b) return -1
        else if (a > b) return 1
      })
    },
    SET_USER_CANTEEN_PREVIEWS(state, userCanteenPreviews) {
      state.userCanteenPreviews = userCanteenPreviews
    },
    SET_INITIAL_DATA_LOADED(state) {
      state.initialDataLoaded = true
    },
    SET_NOTIFICATION(state, notification) {
      // a notification consists of: message, title, status, undoAction, undoMessage
      notification.id = Math.floor(Math.random() * 10000) // generate random 5 digit id
      state.notifications.push(notification)
    },
    ADD_CANTEEN(state, canteen) {
      state.userCanteenPreviews.push({
        id: canteen.id,
        name: canteen.name,
      })
    },
    UPDATE_CANTEEN(state, canteen) {
      const storedCanteen = state.userCanteenPreviews.find((x) => x.id === canteen.id)
      if (storedCanteen) storedCanteen.name = canteen.name
    },
    REMOVE_CANTEEN(state, id) {
      const index = state.userCanteenPreviews.findIndex((x) => x.id === id)
      state.userCanteenPreviews.splice(index, 1)
    },
    REMOVE_NOTIFICATION(state, notification) {
      const idx = state.notifications.indexOf(notification)
      if (idx > -1) state.notifications.splice(idx, 1)
    },
    REMOVE_NOTIFICATIONS(state) {
      state.notifications = []
    },
    SET_UPCOMING_COMMUNITY_EVENTS(state, events) {
      state.upcomingCommunityEvents = events
    },
    SET_VIDEO_TUTORIALS(state, events) {
      state.videoTutorials = events
    },
    SET_SHOW_WEBINAIRE_BANNER(state, showWebinaireBanner) {
      state.showWebinaireBanner = showWebinaireBanner
    },
    SET_PARTNER_TYPES(state, types) {
      state.partnerTypes = types
    },
    SET_LINE_MINISTRIES(state, ministries) {
      state.lineMinistries = ministries.map((m) => ({ value: m.value, text: m.name }))
    },
    SET_USER_PERMISSIONS(state, { permissions, groups }) {
      state.userPermissions = permissions
      state.userGroups = groups
    },

    // Service mutations
    SET_SERVICES(state, services) {
      state.services = services
    },
    SET_SERVICES_LOADING_STATUS(state, status) {
      state.servicesLoadingStatus = status
    },
    SET_SERVICES_ERROR(state, error) {
      state.servicesError = error
    },
    ADD_SERVICE(state, service) {
      state.services.push(service)
    },
    UPDATE_SERVICE(state, service) {
      const idx = state.services.findIndex((s) => s.id === service.id)
      if (idx !== -1) Vue.set(state.services, idx, service)
    },
    REMOVE_SERVICE(state, id) {
      const idx = state.services.findIndex((s) => s.id === id)
      if (idx !== -1) state.services.splice(idx, 1)
    },

    // Canteen service mutations
    SET_CANTEEN_SERVICES(state, services) {
      state.canteenServices = services
    },
    SET_CANTEEN_SERVICES_LOADING_STATUS(state, status) {
      state.canteenServicesLoadingStatus = status
    },
    SET_CANTEEN_SERVICES_ERROR(state, error) {
      state.canteenServicesError = error
    },
    SET_ABSENCES(state, absences) {
      state.absences = absences
    },
    SET_ABSENCE_LEVELS(state, levels) {
      state.absenceLevels = levels
    },
    ADD_ABSENCE(state, absence) {
      state.absences.push(absence)
    },
    ADD_ABSENCE_LEVEL(state, level) {
      state.absenceLevels.push(level)
    },
  },

  actions: {
    // ServiceLevel actions (CRUD, simplified)
    fetchServiceLevels(_, serviceId) {
      const url = serviceId ? `/api/v1/serviceLevels/?service=${serviceId}` : "/api/v1/serviceLevels/"
      return fetch(url).then(verifyResponse)
    },

    createServiceLevel(_, { payload }) {
      return fetch("/api/v1/serviceLevels/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateServiceLevel(_, { id, payload }) {
      return fetch(`/api/v1/serviceLevels/${id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteServiceLevel(_, { id }) {
      return fetch(`/api/v1/serviceLevels/${id}/`, {
        method: "DELETE",
        headers,
      }).then((response) => response.json())
    },

    // Service actions (simplified)
    fetchServices() {
      return fetch("/api/v1/services/").then((response) => response.json())
    },

    createService(_, { payload }) {
      return fetch("/api/v1/services/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then((response) => response.json())
    },

    updateService(_, { id, payload }) {
      return fetch(`/api/v1/services/${id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }).then((response) => response.json())
    },

    deleteService(_, { id }) {
      return fetch(`/api/v1/services/${id}/`, {
        method: "DELETE",
        headers,
      }).then((response) => response.json())
    },

    // Canteen services actions
    fetchCanteenServices({ commit }, canteenId) {
      commit("SET_CANTEEN_SERVICES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteenId}/services/`, {
        credentials: "same-origin",
      })
        .then(verifyResponse)
        .then((services) => {
          commit("SET_CANTEEN_SERVICES", services)
          commit("SET_CANTEEN_SERVICES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return services
        })
        .catch((error) => {
          console.error("fetchCanteenServices", error)
          commit("SET_CANTEEN_SERVICES_ERROR", error.message || "Erreur lors du chargement des services")
          commit("SET_CANTEEN_SERVICES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw error
        })
    },

    // Meal feedback actions (capacité meal-feedback)
    // Un retour repas est une donnée personnelle : ne jamais en journaliser le contenu.
    fetchMyMealFeedback(_, { canteenId, date, serviceId }) {
      const params = new URLSearchParams({ mine: "true" })
      if (date) params.append("date", date)
      if (serviceId) params.append("service", serviceId)
      return fetch(`/api/v1/canteen/${canteenId}/mealFeedback/?${params.toString()}`, {
        credentials: "same-origin",
      })
        .then(verifyResponse)
        .then((feedbacks) => (Array.isArray(feedbacks) && feedbacks.length ? feedbacks[0] : null))
    },

    createMealFeedback(_, { canteenId, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/mealFeedback/`, {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateMealFeedback(_, { canteenId, id, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/mealFeedback/${id}/`, {
        method: "PATCH",
        credentials: "same-origin",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    fetchMealFeedbackStats(_, { canteenId, startDate, endDate, serviceId } = {}) {
      const params = new URLSearchParams()
      if (startDate) params.append("start_date", startDate)
      if (endDate) params.append("end_date", endDate)
      if (serviceId) params.append("service", serviceId)
      const query = params.toString()
      return fetch(`/api/v1/canteen/${canteenId}/mealFeedbackStats/${query ? "?" + query : ""}`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    fetchSupervisorFeedbackStats(_, { canteenId, date } = {}) {
      const params = new URLSearchParams()
      if (date) params.append("date", date)
      const query = params.toString()
      return fetch(`/api/v1/canteen/${canteenId}/supervisorFeedbackStats/${query ? "?" + query : ""}`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Absence and AbsenceLevel actions
    fetchAbsences({ commit }, { canteenId, date } = {}) {
      const params = new URLSearchParams()
      if (canteenId) params.append("canteen", canteenId)
      if (date) params.append("date", date)
      const query = params.toString()
      return fetch(`/api/v1/absences/${query ? "?" + query : ""}`)
        .then((response) => response.json())
        .then((absences) => {
          commit("SET_ABSENCES", absences)
          return absences
        })
    },
    createAbsence({ commit }, payload) {
      return fetch("/api/v1/absences/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((absence) => {
          commit("ADD_ABSENCE", absence)
          return absence
        })
    },
    updateAbsence({ commit }, payload) {
      return fetch(`/api/v1/absences/${payload.id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((absence) => {
          commit("UPDATE_ABSENCE", absence)
          return absence
        })
    },
    deleteAbsence({ commit }, id) {
      return fetch(`/api/v1/absences/${id}/`, {
        method: "DELETE",
        headers,
      }).then((absence) => {
        commit("DELETE_ABSENCE", absence)
        return absence
      })
    },

    fetchAbsenceById(_, id) {
      return fetch(`/api/v1/absences/${id}/`).then((response) => response.json())
    },
    fetchAbsenceLevels({ commit }, absenceId) {
      let url = "/api/v1/absenceLevels/"
      if (absenceId) {
        url += `?absence=${absenceId}`
      }
      return fetch(url)
        .then((response) => response.json())
        .then((levels) => {
          commit("SET_ABSENCE_LEVELS", levels)
          return levels
        })
    },
    createAbsenceLevel({ commit }, payload) {
      // payload should include absence, level, nb_standard_meals, nb_no_pork_meals, nb_vegetarian_meals
      return fetch("/api/v1/absenceLevels/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((level) => {
          commit("ADD_ABSENCE_LEVEL", level)
          return level
        })
    },
    updateAbsenceLevel({ commit }, payload) {
      // payload should include id and updated fields
      return fetch(`/api/v1/absenceLevels/${payload.id}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((level) => {
          commit("UPDATE_ABSENCE_LEVEL", level)
          return level
        })
    },
    deleteAbsenceLevel({ commit }, id) {
      return fetch(`/api/v1/absenceLevels/${id}/`, {
        method: "DELETE",
        headers,
      }).then(() => {
        commit("DELETE_ABSENCE_LEVEL", id)
        return id
      })
    },
    fetchLoggedUser(context) {
      context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/loggedUser/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_LOGGED_USER", response || null)
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          if (context.state.loggedUser && window.$crisp) {
            window.$crisp.push(["set", "user:email", [context.state.loggedUser.email]])
          }
        })
        .catch((e) => {
          console.error("fetchLoggedUser", e)
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.ERROR)
        })
    },

    // It is possible to implement a cache at this level
    fetchCanteen(context, { id }) {
      return fetch(`/api/v1/canteens/${id}`).then((response) => {
        if (response.status != 200) throw new Error()
        return response.json()
      })
    },

    fetchCanteenSummary() {
      return fetch(`/api/v1/canteenSummaries/`).then((response) => {
        if (response.status < 200 || response.status >= 400) {
          throw new Error(`Error encountered : ${response.status}`)
        }
        return response.json()
      })
    },

    fetchSectors(context) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/sectors/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_SECTORS", response)
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          console.log("fetchSectors", e)
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
        })
    },

    fetchPartnerTypes(context) {
      return fetch("/api/v1/partnerTypes/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PARTNER_TYPES", response)
        })
        .catch((e) => {
          console.log("fetchPartnerTypes", e)
        })
    },

    fetchUserCanteenPreviews(context) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/canteenPreviews/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_USER_CANTEEN_PREVIEWS", response)
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          console.log("fetchUserCanteenPreviews", e)
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
        })
    },

    fetchBlogPosts(context, { limit = 6, offset, tag, search }) {
      let url = `/api/v1/blogPosts/?limit=${limit}&offset=${offset}`
      if (tag) url += `&tag=${tag}`
      if (search) url += `&search=${search}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },

    fetchWasteActions(context, { limit = 6, offset, filters }) {
      let url = `/api/v1/wasteActions/?limit=${limit}&offset=${offset}`
      Object.keys(filters).forEach((filterKey) => {
        if (filters[filterKey].value && filters[filterKey].value.length) {
          if (filters[filterKey].value) {
            if (Array.isArray(filters[filterKey].value)) {
              filters[filterKey].value.forEach((filterValue) => {
                url += `&${filters[filterKey].apiKey}=${filterValue}`
              })
            } else {
              url += `&${filters[filterKey].apiKey}=${filters[filterKey].value}`
            }
          }
        }
      })
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchAllSurveys() {
      let url = `/api/v1/allSurveys/`
      // if (search) url += `&search=${search}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchSurveyLinks(context, { limit = 6, offset, search }) {
      let url = `/api/v1/studysurveys/?limit=${limit}&offset=${offset}`
      if (search) url += `&search=${search}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    // Restitution cadree sur une activite. Elle remplace le tableau de bord d'administration de
    // `djf_surveys`, qui agregeait toutes cantines et toutes campagnes confondues et n'etait
    // ouvert qu'aux auteurs du referentiel.
    fetchActionResponses(context, { actionId }) {
      return fetch(`/api/v1/action/${actionId}/responses/`).then(verifyResponse)
    },
    fetchSurveyDetails(context, { id }) {
      let url = `/api/v1/surveyDetails/?id=${id}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    CreateConcerns(context, { payload }) {
      return fetch(`/api/v1/issues/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },
    CreateEatingExperience(context, { payload }) {
      return fetch(`/api/v1/eatingExperiences/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },
    fetchAllStudySurveys(context, { params }) {
      let url = `/api/v1/eatingExperiences/`
      if (params) {
        url += `?issue=${params}`
      }
      // if (search) url += `&search=${search}`
      return fetch(url, { method: "GET" })
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchAction(context, { limit = 6, offset, search }) {
      let url = `/api/v1/action/?limit=${limit}&offset=${offset}`
      if (search) url += `&search=${search}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchProspectTestimonials(context, { limit = 6, offset, search }) {
      let url = `/api/v1/prospect-testimonials/?limit=${limit}&offset=${offset}`
      if (search) url += `&search=${search}`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchActionDetails(context, { id }) {
      // La barre finale est celle de la route : sans elle, chaque appel coûte une redirection 301.
      let url = `/api/v1/action/${id}/`
      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },
    fetchRecipes(context, { limit = 6, offset, search }) {
      let url = `/api/v1/recipes/?limit=${limit}&offset=${offset}`
      if (search) url += `&search=${search}`

      return fetch(url)
        .then(verifyResponse)
        .then((response) => {
          return response
        })
    },

    // payload: { canteenId: int, isDone: boolean }
    createOrUpdateResourceAction(context, { resourceId, payload }) {
      return fetch(`/api/v1/wasteActions/${resourceId}/actions`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },
    CreateAction(context, { payload }) {
      return fetch(`/api/v1/createAction/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },
    fetchAllConcerns() {
      return fetch("/api/v1/issues/")
        .then((response) => response.json())
        .then((data) => data)
    },
    fetchInitialData(context) {
      context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      context.commit("SET_COMMUNITY_EVENTS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      context.commit("SET_VIDEO_TUTORIALS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/initialData/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_LOGGED_USER", response.loggedUser)
          context.commit("SET_SECTORS", response.sectors)
          context.commit("SET_PARTNER_TYPES", response.partnerTypes)
          context.commit("SET_UPCOMING_COMMUNITY_EVENTS", response.communityEvents)
          context.commit("SET_VIDEO_TUTORIALS", response.videoTutorials)
          context.commit("SET_USER_CANTEEN_PREVIEWS", response.canteenPreviews)
          context.commit("SET_LINE_MINISTRIES", response.lineMinistries)
          // context.commit("", response.permissions)
          context.commit("SET_USER_PERMISSIONS", {
            permissions: response.permissions?.permissions || [],
            groups: response.permissions?.groups || [],
          })
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("SET_COMMUNITY_EVENTS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("SET_VIDEO_TUTORIALS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .then(() => {
          const criticalLoadingStatuses = ["canteensLoadingStatus"]
          const hasError = criticalLoadingStatuses.some((x) => context.state[x] === Constants.LoadingStatus.ERROR)
          if (hasError) throw new Error("Une erreur s'est produite lors du chargement des données intiales")
          else context.commit("SET_INITIAL_DATA_LOADED")
        })
    },

    updateProfile(context, { payload }) {
      context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/user/${context.state.loggedUser.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_LOGGED_USER", response)
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    changePassword(context, { payload }) {
      context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/passwordChange/", { method: "PUT", headers, body: JSON.stringify(payload) })
        .then((response) => {
          if (response.status === 400) {
            return response.json().then((jsonResponse) => {
              throw new Error(Object.values(jsonResponse).join(", "))
            })
          }
          return verifyResponse(response)
        })
        .then(() => {
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          context.commit("SET_USER_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    createCanteen(context, { payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/`, { method: "POST", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("ADD_CANTEEN", response)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    updateCanteen(context, { id, payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${id}`, { method: "PATCH", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("UPDATE_CANTEEN", response)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    publishCanteen(context, { id, payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${id}/publish`, { method: "POST", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    unpublishCanteen(context, { id, payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${id}/unpublish`, { method: "POST", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    deleteCanteen(context, { id }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${id}`, { method: "DELETE", headers })
        .then(verifyResponse)
        .then(() => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          context.commit("REMOVE_CANTEEN", id)
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    createDiagnostic(context, { canteenId, payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${canteenId}/diagnostics/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    updateDiagnostic(context, { canteenId, id, payload }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteens/${canteenId}/diagnostics/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    saveLocalStorageDiagnostic(context, diagnostic) {
      let savedDiagnostics = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}")
      savedDiagnostics[diagnostic.year] = diagnostic
      return localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedDiagnostics))
    },

    removeLocalStorageDiagnostics() {
      return localStorage.removeItem(LOCAL_STORAGE_KEY)
    },

    subscribeNewsletter(context, email) {
      return fetch("/api/v1/subscribeNewsletter/", { method: "POST", headers, body: JSON.stringify({ email }) }).then(
        verifyResponse
      )
    },

    addManager(context, { canteenId, email }) {
      return fetch(`/api/v1/addManager/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    sendCanteenEmail(context, payload) {
      return fetch("/api/v1/message/", { method: "POST", headers, body: JSON.stringify(payload) }).then(verifyResponse)
    },

    sendCanteenNotFoundEmail(context, payload) {
      return fetch("/api/v1/canteenNotFoundMessage/", { method: "POST", headers, body: JSON.stringify(payload) }).then(
        verifyResponse
      )
    },

    sendCanteenTeamRequest(context, { canteenId, payload }) {
      return fetch(`/api/v1/teamJoinRequest/${canteenId}/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    removeManager(context, { canteenId, email }) {
      return fetch(`/api/v1/removeManager/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    addStaff(context, { canteenId, email, job }) {
      return fetch(`/api/v1/addStaff/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email, job }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    removeStaff(context, { canteenId, email }) {
      return fetch(`/api/v1/removeStaff/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    fetchConsumers(context, { canteenId }) {
      return fetch(`/api/v1/canteens/${canteenId}/consumers/`).then(verifyResponse)
    },

    addConsumer(context, { canteenId, email, firstName, lastName }) {
      return fetch(`/api/v1/addConsumer/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email, firstName, lastName }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    removeConsumer(context, { canteenId, email }) {
      return fetch(`/api/v1/removeConsumer/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ canteenId, email }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    importConsumers(context, { canteenId, file }) {
      let form = new FormData()
      form.append("file", file)
      form.append("canteenId", canteenId)
      return fetch(`/api/v1/importConsumers/`, {
        method: "POST",
        headers: {
          "X-CSRFToken": window.CSRF_TOKEN || "",
        },
        body: form,
      }).then(verifyResponse)
    },

    importDiagnostics(context, { importLevel, payload }) {
      let form = new FormData()
      form.append("file", payload.file)
      const importLevels = {
        COMPLETE: "complete",
        SIMPLE: "simple",
        NONE: "simple",
        CC_SIMPLE: "ccSimple",
        CC_COMPLETE: "ccComplete",
      }
      return fetch(`/api/v1/importDiagnostics/${importLevels[importLevel]}/`, {
        method: "POST",
        headers: {
          "X-CSRFToken": window.CSRF_TOKEN || "",
        },
        body: form,
      }).then(verifyResponse)
    },

    importPurchases(context, payload) {
      let form = new FormData()
      form.append("file", payload.file)
      return fetch(`/api/v1/importPurchases/`, {
        method: "POST",
        headers: {
          "X-CSRFToken": window.CSRF_TOKEN || "",
        },
        body: form,
      }).then(verifyResponse)
    },

    notify(context, { title, message, status, undoAction, undoMessage }) {
      context.commit("SET_NOTIFICATION", { title, message, status, undoAction, undoMessage })
    },
    notifyRequiredFieldsError(context) {
      const title = null
      const message = "Merci de vérifier les champs en rouge et réessayer"
      const status = "error"
      context.dispatch("notify", { title, message, status })
    },
    notifyServerError(context, error) {
      const title = "Oops !"
      const message =
        error instanceof AuthenticationError
          ? "Votre session a expiré. Rechargez la page et reconnectez-vous pour continuer."
          : "Une erreur est survenue, vous pouvez réessayer plus tard ou nous contacter directement à support-egalim@beta.gouv.fr"
      const status = "error"
      console.log("notify", { title, message, status })
      // context.dispatch("notify", { title, message, status })
    },
    removeNotification(context, notification) {
      context.commit("REMOVE_NOTIFICATION", notification)
    },
    removeNotifications(context) {
      context.commit("REMOVE_NOTIFICATIONS")
    },

    submitTeledeclaration(context, { id }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      const payload = {
        diagnosticId: id,
      }
      return fetch("/api/v1/teledeclaration/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((diagnostic) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return diagnostic
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    createAndPrefillDiagnostics(context, { year, ids }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      const payload = {
        canteenIds: ids,
      }
      return fetch(`/api/v1/createDiagnosticsFromPurchases/${year}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    submitMultiplePublications(context, { ids }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/publish/", {
        method: "POST",
        headers,
        body: JSON.stringify({ ids }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    cancelTeledeclaration(context, { id }) {
      context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/teledeclaration/${id}/cancel/`, {
        method: "POST",
        headers,
      })
        .then(verifyResponse)
        .then((diagnostic) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return diagnostic
        })
        .catch((e) => {
          context.commit("SET_CANTEENS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    sendInquiryEmail(_, payload) {
      return fetch("/api/v1/inquiry/", { method: "POST", headers, body: JSON.stringify(payload) }).then(verifyResponse)
    },

    createPurchase(context, { payload }) {
      context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/purchases/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    updatePurchase(context, { id, payload }) {
      context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/purchases/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    deletePurchase(context, { id }) {
      context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/purchases/${id}`, {
        method: "DELETE",
        headers,
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    deletePurchases(context, { ids }) {
      context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/purchases/delete/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    restorePurchases(context, ids) {
      context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/purchases/restore/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids }),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_PURCHASES_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    fetchReservationExpe(context, { canteen }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/reservationExpe/`)
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    createReservationExpe(context, { canteen, payload }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/reservationExpe/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    updateReservationExpe(context, { canteen, payload }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/reservationExpe/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    fetchVegetarianExpe(context, { canteen }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/vegetarianExpe/`)
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    fetchUserPermissions({ commit }) {
      return fetch("/api/v1/permissions/", {
        method: "GET",
        headers,
      })
        .then(verifyResponse)
        .then((response) => {
          commit("SET_USER_PERMISSIONS", {
            permissions: response.permissions,
            groups: response.groups,
          })
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des permissions", error)
        })
    },

    createVegetarianExpe(context, { canteen, payload }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/vegetarianExpe/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    updateVegetarianExpe(context, { canteen, payload }) {
      context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch(`/api/v1/canteen/${canteen.id}/vegetarianExpe/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      })
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
          return response
        })
        .catch((e) => {
          context.commit("SET_EXPE_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    createReview(context, { payload }) {
      return fetch("/api/v1/reviews/", { method: "POST", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          context.dispatch("fetchLoggedUser") // refresh reviews for user
          return response
        })
        .catch((e) => {
          throw e
        })
    },

    fetchUpcomingCommunityEvents(context) {
      context.commit("SET_COMMUNITY_EVENTS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/communityEvents/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_UPCOMING_COMMUNITY_EVENTS", response)
          context.commit("SET_COMMUNITY_EVENTS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          console.log("fetchUpcomingCommunityEvents", e)
          context.commit("SET_COMMUNITY_EVENTS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    fetchVideoTutorials(context) {
      context.commit("SET_VIDEO_TUTORIALS_LOADING_STATUS", Constants.LoadingStatus.LOADING)
      return fetch("/api/v1/videoTutorials/")
        .then(verifyResponse)
        .then((response) => {
          context.commit("SET_VIDEO_TUTORIALS", response)
          context.commit("SET_VIDEO_TUTORIALS_LOADING_STATUS", Constants.LoadingStatus.SUCCESS)
        })
        .catch((e) => {
          console.log("fetchVideoTutorials", e)
          context.commit("SET_VIDEO_TUTORIALS_LOADING_STATUS", Constants.LoadingStatus.ERROR)
          throw e
        })
    },

    claimCanteen(context, { canteenId }) {
      return fetch(`/api/v1/canteens/${canteenId}/claim/`, { method: "POST", headers }).then(verifyResponse)
    },

    undoClaimCanteen(context, { canteenId }) {
      return fetch(`/api/v1/canteens/${canteenId}/undoClaim/`, { method: "POST", headers }).then(verifyResponse)
    },

    followCanteen(context, { canteenId }) {
      return fetch(`/api/v1/canteens/${canteenId}/addFollower/`, { method: "POST", headers }).then(verifyResponse)
    },

    unfollowCanteen(context, { canteenId }) {
      return fetch(`/api/v1/canteens/${canteenId}/DeleteFollower/`, { method: "POST", headers }).then(verifyResponse)
    },

    addSatellite(context, { id, payload }) {
      return fetch(`/api/v1/canteens/${id}/satellites/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then((response) => {
        return response.json().then((jsonResponse) => {
          if (response.status < 200 || response.status >= 400) throw new Error(jsonResponse.detail)
          return jsonResponse
        })
      })
    },

    setShowWebinaireBanner(context, showWebinaireBanner) {
      context.commit("SET_SHOW_WEBINAIRE_BANNER", showWebinaireBanner)
    },

    createPartner(context, { payload }) {
      return fetch("/api/v1/partners/", { method: "POST", headers, body: JSON.stringify(payload) })
        .then(verifyResponse)
        .then((response) => {
          return response
        })
        .catch((e) => {
          throw e
        })
    },

    startAction(context, { studyId }) {
      return fetch(`/api/v1/action/${studyId}/start`, { method: "POST", headers }).then(verifyResponse)
    },

    engageInAction(context, { studyId }) {
      return fetch(`/api/v1/action/${studyId}/participate`, { method: "POST", headers }).then(verifyResponse)
    },

    publishAction(_, { studyId }) {
      return fetch(`/api/v1/action/${studyId}/publish`, { method: "POST", headers }).then(verifyResponse)
    },

    // Écran d'apprentissage du convive, en une lecture : identité, progression, les trois piliers
    // et le bandeau de défi viennent du même appel. Les composer côté client laisserait l'écran
    // afficher un niveau et un compteur qui ne parlent pas de la même chose (§9.7).
    fetchPedagogicalBoard() {
      return fetch("/api/v1/pedagogicalBoard/").then(verifyResponse)
    },

    fetchPedagogicalModule(_, { actionId }) {
      return fetch(`/api/v1/pedagogicalModules/${actionId}`).then(verifyResponse)
    },

    // --- Cours : lecture par le convive ---------------------------------------------------------
    // Le sommaire, la progression et la reprise viennent du même appel. Les recomposer côté client
    // à partir des cartes du tableau ferait diverger l'avancement affiché de celui qui est
    // enregistré.
    fetchPedagogicalCourse(_, { courseId }) {
      return fetch(`/api/v1/pedagogicalCourses/${courseId}`).then(verifyResponse)
    },

    // La solution n'est demandée qu'explicitement : la joindre d'emblée ferait d'un entraînement
    // une lecture.
    fetchPedagogicalChapter(_, { chapterId, withSolution }) {
      const query = withSolution ? "?solution=1" : ""
      return fetch(`/api/v1/pedagogicalChapters/${chapterId}${query}`).then(verifyResponse)
    },

    // `evidence` porte ce que l'apprenant avance quand le chapitre exige une preuve. Sans preuve
    // exigée, le corps est vide et le chapitre est franchi sur-le-champ ; avec, la tentative reste
    // en attente jusqu'à l'attestation d'un superviseur.
    completePedagogicalChapter(_, { chapterId, evidence }) {
      return fetch(`/api/v1/pedagogicalChapters/${chapterId}/complete`, {
        method: "POST",
        headers,
        body: JSON.stringify(evidence || {}),
      }).then(verifyResponse)
    },

    // --- Attestation des preuves par le superviseur ----------------------------------------------
    // Chemin **nominal** de S27 : un superviseur connecté atteste depuis sa file. Le lien signé
    // reçu par courriel reste ouvert comme chemin de rattrapage, hors de l'API.
    fetchPendingEvidences() {
      return fetch("/api/v1/pendingEvidences/").then(verifyResponse)
    },

    attestEvidence(_, { evidenceId }) {
      return fetch(`/api/v1/evidences/${evidenceId}/attest/`, { method: "POST", headers }).then(verifyResponse)
    },

    fetchCourseCertificate(_, { courseId }) {
      return fetch(`/api/v1/pedagogicalCourses/${courseId}/certificate`).then(verifyResponse)
    },

    // --- Cours : composition par le responsable pédagogique --------------------------------------
    // Hors habilitation, ces appels répondent 404 : l'atelier n'apprend pas qu'un cours existe
    // ailleurs. Le front ne filtre donc rien de son côté.

    // Les établissements où l'appelant est habilité. L'atelier ne devine pas d'identifiant : sans
    // cette liste, créer un cours reviendrait à saisir un numéro de cantine à la main.
    fetchPedagogicalCanteens() {
      return fetch("/api/v1/pedagogicalCanteens/").then(verifyResponse)
    },

    fetchAuthoredCourses() {
      return fetch("/api/v1/authoredCourses/").then(verifyResponse)
    },

    fetchAuthoredCourse(_, { courseId }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/`).then(verifyResponse)
    },

    createAuthoredCourse(_, { payload }) {
      return fetch("/api/v1/authoredCourses/", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateAuthoredCourse(_, { courseId, payload }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteAuthoredCourse(_, { courseId }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/`, { method: "DELETE", headers }).then(verifyResponse)
    },

    // Publie, ou refuse en **nommant** la partie vide : le message d'erreur est remonté tel quel à
    // l'auteur, qui sait alors où revenir.
    publishAuthoredCourse(_, { courseId }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/publish/`, { method: "POST", headers }).then(verifyResponse)
    },

    fetchAuthoredParts(_, { courseId }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/parts/`).then(verifyResponse)
    },

    fetchAuthoredPart(_, { partId }) {
      return fetch(`/api/v1/authoredParts/${partId}/`).then(verifyResponse)
    },

    createAuthoredPart(_, { courseId, payload }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/parts/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateAuthoredPart(_, { partId, payload }) {
      return fetch(`/api/v1/authoredParts/${partId}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteAuthoredPart(_, { partId }) {
      return fetch(`/api/v1/authoredParts/${partId}/`, { method: "DELETE", headers }).then(verifyResponse)
    },

    reorderAuthoredParts(_, { courseId, order }) {
      return fetch(`/api/v1/authoredCourses/${courseId}/reorder/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ order }),
      }).then(verifyResponse)
    },

    fetchAuthoredChapters(_, { partId }) {
      return fetch(`/api/v1/authoredParts/${partId}/chapters/`).then(verifyResponse)
    },

    fetchAuthoredChapter(_, { chapterId }) {
      return fetch(`/api/v1/authoredChapters/${chapterId}/`).then(verifyResponse)
    },

    createAuthoredChapter(_, { partId, payload }) {
      return fetch(`/api/v1/authoredParts/${partId}/chapters/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateAuthoredChapter(_, { chapterId, payload }) {
      return fetch(`/api/v1/authoredChapters/${chapterId}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteAuthoredChapter(_, { chapterId }) {
      return fetch(`/api/v1/authoredChapters/${chapterId}/`, { method: "DELETE", headers }).then(verifyResponse)
    },

    reorderAuthoredChapters(_, { partId, order }) {
      return fetch(`/api/v1/authoredParts/${partId}/reorder/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ order }),
      }).then(verifyResponse)
    },

    fetchAuthoredResources(_, { chapterId }) {
      return fetch(`/api/v1/authoredChapters/${chapterId}/resources/`).then(verifyResponse)
    },

    createAuthoredResource(_, { chapterId, payload }) {
      return fetch(`/api/v1/authoredChapters/${chapterId}/resources/`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteAuthoredResource(_, { resourceId }) {
      return fetch(`/api/v1/authoredResources/${resourceId}/`, { method: "DELETE", headers }).then(verifyResponse)
    },

    logout() {
      return fetch("/se-deconnecter", { method: "POST", headers })
        .then((response) => {
          if (response.redirected) window.location.href = response.url
        })
        .catch((e) => {
          throw e
        })
    },

    // Waste Measurement actions
    saveServiceWasteMeasurement(_, { canteenId, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteMeasurements/`, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la sauvegarde du gaspillage`)
        return response.json()
      })
    },

    updateServiceWasteMeasurement(_, { canteenId, measurementId, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteMeasurements/${measurementId}/`, {
        method: "PATCH",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la modification du gaspillage`)
        return response.json()
      })
    },

    fetchServiceWasteMeasurementYesterday(_, { canteenId, refDate, serviceId }) {
      const params = new URLSearchParams()
      if (refDate) params.append("ref_date", refDate)
      if (serviceId) params.append("service_id", serviceId)
      const query = params.toString()
      const url = `/api/v1/canteen/${canteenId}/wasteMeasurements/yesterday/${query ? "?" + query : ""}`
      return fetch(url, { credentials: "same-origin" }).then((response) => {
        if (response.status === 404) return null
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la récupération J-1`)
        return response.json()
      })
    },

    fetchServiceWasteMeasurementsForDate(_, { canteenId, params }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteMeasurements/?${params}`, {
        credentials: "same-origin",
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la récupération des mesures`)
        return response.json()
      })
    },

    // Proposition de préremplissage du bilan à partir des pesées Labricot de l'année.
    // Lecture seule : l'endpoint n'écrit rien, c'est l'utilisateur qui valide le formulaire.
    fetchTeledeclarationPrefill(_, { canteenId, year }) {
      return fetch(`/api/v1/canteen/${canteenId}/teledeclarationPrefill/?year=${year}`, {
        credentials: "same-origin",
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors du calcul du préremplissage`)
        return response.json()
      })
    },

    // Provenance des champs du bilan : ce qui vient des pesées, ce qui a été saisi (S43b).
    fetchDiagnosticProvenance(_, { canteenId, year }) {
      return fetch(`/api/v1/canteen/${canteenId}/diagnosticProvenance/${year}/`, {
        credentials: "same-origin",
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la lecture de la provenance`)
        return response.json()
      })
    },

    // Trace un report appliqué. C'est le geste du gestionnaire qui écrit, jamais la lecture
    // du préremplissage : tant que rien n'est reporté, rien n'est enregistré.
    recordDiagnosticProvenance(_, { canteenId, year, fields, measurementIds }) {
      return fetch(`/api/v1/canteen/${canteenId}/diagnosticProvenance/${year}/`, {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: JSON.stringify({ fields, measurementIds: measurementIds || [] }),
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de l'enregistrement de la provenance`)
        return response.json()
      })
    },

    // --- Campagne de mesure du gaspillage sur une période (S40, S41) ---
    //
    // Tout l'accès réseau du tunnel passe par ces actions : aucun `fetch` dans les étapes.
    // Elles s'appuient sur `verifyResponse`, qui lève un `BadRequestError` portant le corps du
    // 400 — c'est ce qui permet au tunnel de rattacher chaque message à son champ plutôt que
    // d'afficher une bannière indistincte.
    fetchWasteCampaigns(_, { canteenId }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteCampaigns/`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Proposition de report des pesées par service sur la période. Lecture seule : l'endpoint
    // n'écrit rien, c'est l'utilisateur qui retient ou non la masse suggérée.
    fetchWasteCampaignPrefill(_, { canteenId, startDate, endDate }) {
      const params = new URLSearchParams({ startDate, endDate })
      return fetch(`/api/v1/canteen/${canteenId}/wasteCampaigns/prefill/?${params}`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    saveWasteCampaign(_, { canteenId, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteCampaigns/`, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    updateWasteCampaign(_, { canteenId, campaignId, payload }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteCampaigns/${campaignId}/`, {
        method: "PATCH",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      }).then(verifyResponse)
    },

    deleteWasteCampaign(_, { canteenId, campaignId }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteCampaigns/${campaignId}/`, {
        method: "DELETE",
        headers,
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Restitution du diagnostic SWOT : thématiques, matrice, frise de maturité et recommandations.
    // Tout est calculé côté backend — l'écran n'a ni score, ni quadrant, ni seuil à connaître.
    fetchSwotDiagnostic(_, { canteenId }) {
      return fetch(`/api/v1/canteen/${canteenId}/swotDiagnostic/`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Efficacité d'une action sur une cantine (avant / après). La réponse n'est qu'un agrégat
    // d'établissement — moyennes et effectifs, jamais une réponse individuelle.
    fetchActionEfficacy(_, { canteenId, actionId }) {
      return fetch(`/api/v1/canteen/${canteenId}/action/${actionId}/efficacy/`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Manifestation d'intérêt pour une fonctionnalité à l'étude. Le geste n'engage rien : il
    // mesure la demande. La réponse ne rapporte qu'un compteur, jamais une identité.
    // Diagnostic de gaspillage : les deux questionnaires d'amorçage (§10.5 volet B). La réponse
    // dit, question par question, ce qui a été déduit de la collecte et ce qui reste à déclarer —
    // une question déduite n'est pas reposée.
    fetchWasteDiagnostic(_, { canteenId, year }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteDiagnostic/${year}/`, {
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Le POST rend le diagnostic relu : l'écran repart de ce que le serveur a retenu, jamais de
    // ce qu'il croyait avoir envoyé.
    saveWasteDiagnostic(_, { canteenId, year, answers }) {
      return fetch(`/api/v1/canteen/${canteenId}/wasteDiagnostic/${year}/`, {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify({ answers }),
      }).then(verifyResponse)
    },

    signalSwotRecommendationInterest(_, { canteenId, recommendationId }) {
      return fetch(`/api/v1/canteen/${canteenId}/swotRecommendations/${recommendationId}/interest/`, {
        method: "POST",
        headers,
        credentials: "same-origin",
      }).then(verifyResponse)
    },

    // Items de menu importés pour une date (EPIC 0D) — alimente le variant gaspillage « menu ».
    fetchMenuItems(_, { canteenId, date }) {
      const query = date ? `?date=${date}` : ""
      return fetch(`/api/v1/canteen/${canteenId}/menuItems/${query}`, {
        credentials: "same-origin",
      }).then((response) => {
        if (!response.ok) throw new Error(`Erreur ${response.status} lors de la récupération du menu`)
        return response.json()
      })
    },
  },

  getters: {
    getLocalDiagnostics: () => () => {
      const savedDiagnostics = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (!savedDiagnostics) {
        return diagnosticYears().map((year) => Object.assign({}, Constants.DefaultDiagnostics, { year }))
      }
      return Object.values(JSON.parse(savedDiagnostics))
    },
    getCanteenUrlComponent: () => (canteen) => {
      return `${canteen.id}--${canteen.name}`
    },
    getPartnerUrlComponent: () => (partner) => {
      return `${partner.id}--${partner.name}`
    },
    userPermissions: (state) => state.userPermissions,
    userGroups: (state) => state.userGroups,
    // Authentifié = un loggedUser est chargé (l'app teste sinon store.state.loggedUser en dur partout).
    isAuthenticated: (state) => !!state.loggedUser,
    // Rôles effectifs de l'utilisateur : ce sont les *rattachements* qui portent les rôles
    // cantine (superviseur, employé, cuisinier…). `job` ne reste lu que pour les professions
    // qu'aucun rattachement ne matérialise (MANAGER via canteens, CONSUMER, RESEARCHER…).
    userRoles: (state) => {
      const user = state.loggedUser
      if (!user) return []
      const roles = (user.canteenMemberships || []).map((membership) => membership.job)
      if (user.job && !PIVOT_ROLES.includes(user.job)) roles.push(user.job)
      return Array.from(new Set(roles))
    },
    hasRole: (state, getters) => (role) => getters.userRoles.includes(role),
    // Rôles rattachés dans une cantine donnée (portée par cantine, pas globale).
    rolesInCanteen: (state) => (canteenId) => {
      const user = state.loggedUser
      if (!user || !canteenId) return []
      return (user.canteenMemberships || [])
        .filter((membership) => membership.canteen === canteenId)
        .map((membership) => membership.job)
    },
    // Rôle Labricot = rôle pris en charge par le nouveau shell (barre fine + drawer).
    isLabricotRole: (state, getters) => LABRICOT_ROLES.some((role) => getters.userRoles.includes(role)),
    contextCanteen: (state) => state.contextCanteen,
  },
})
